import { Popover } from "radix-ui";
import { ButtonWrapper } from "./_Button";
import FontAwesome from "../../../utilities/FontAwesome";
import { ChromePicker } from "react-color";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "../../styles/SlateEditor.module.scss";
import { useEditorRef, useEditorSelector } from "platejs/react";
import {
  FontBackgroundColorPlugin,
  FontColorPlugin,
} from "@platejs/basic-styles/react";

const icons = {
  [FontColorPlugin.key]: (
    <FontAwesome fixedWidth prefix={"fas"} name={"fa-font"} />
  ),
  [FontBackgroundColorPlugin.key]: (
    <FontAwesome fixedWidth prefix={"fas"} name={"fa-fill-drip"} />
  ),
};

export function ColorDropdownMenu({ nodeType, tooltip }) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const editor = useEditorRef();

  const selectionDefined = useEditorSelector(
    (editor) => !!editor.selection,
    [],
  );

  const color = useEditorSelector(
    (editor) => editor.api.mark(nodeType) as string,
    [nodeType],
  );

  const [selectedColor, setSelectedColor] = useState<string>();

  const onToggle = useCallback(
    (value = !open) => {
      setOpen(value);
    },
    [open, setOpen],
  );

  const updateColor = useCallback(
    (value: string) => {
      if (editor.selection) {
        setSelectedColor(value);
        editor.tf.select(editor.selection);
        editor.tf.focus();
        editor.tf.addMarks({ [nodeType]: value });
      }
    },
    [editor, nodeType],
  );

  const clearColor = useCallback(() => {
    if (editor.selection) {
      editor.tf.select(editor.selection);
      editor.tf.focus();
      if (selectedColor) {
        editor.tf.removeMarks(nodeType);
      }
      setOpen(false);
    }
  }, [editor, selectedColor, onToggle, nodeType]);

  useEffect(() => {
    if (selectionDefined) {
      setSelectedColor(color);
    }
  }, [color, selectionDefined]);

  return (
    <Popover.Root open={open}>
      <Popover.Trigger asChild>
        <ButtonWrapper
          tooltip={tooltip}
          ref={triggerRef}
          onClick={() => setOpen((v) => !v)}
        >
          {icons[nodeType]}
        </ButtonWrapper>
      </Popover.Trigger>
      <Popover.Content
        onPointerDownOutside={(e) => {
          if (
            triggerRef.current &&
            !triggerRef.current.contains(e.target as HTMLElement)
          ) {
            setOpen(false);
          }
        }}
        className={styles.colorPickerContent}
      >
        <ChromePicker
          color={selectedColor || color}
          onChange={(color) => {
            updateColor(color.hex);
          }}
          className={styles.chromePicker}
        />
        {color && (
          <button className={styles.button} onClick={clearColor}>
            <FontAwesome prefix={"fas"} name={"fa-eraser"} />
            &nbsp;Clear
          </button>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}
